import type {CSSProperties, ReactNode} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Clock, Share} from 'lucide-react';
import {
  Button,
  HeroCard,
  SegmentedProgress,
  Tag,
  type SegmentedProgressSegment,
} from '@chekinapp/ui';

type Props = {
  registered: number;
  pending: number;
  total: number;
  dueDate?: string;
  isComplete: boolean;
  onShare?: () => void;
};

const TIGHT_GAP_ABOVE = 8;

const PILL_CLASS =
  'min-h-9 h-auto px-1.5 py-[5px] lg:min-h-7 lg:py-1 text-[12px] font-semibold [&_svg]:size-[15px] [&_svg]:text-[var(--chekin-color-brand-blue)] [@media(max-width:379px)]:[&_svg]:hidden';

function buildSegments(registered: number, pending: number, total: number) {
  return Array.from({length: total}, (_, index): SegmentedProgressSegment => {
    if (index < registered) return {state: 'done'};
    if (index < registered + pending) return {state: 'warn', fill: 50};
    return {state: 'todo'};
  });
}

function HeroPill({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button variant="inverse" size="sm" className={PILL_CLASS} onClick={onClick}>
      {icon}
      <span>{label}</span>
    </Button>
  );
}

function RegistrationHeroCard({
  registered,
  pending,
  total,
  dueDate,
  isComplete,
  onShare,
}: Props) {
  const {t} = useTranslation();
  const showShare = Boolean(onShare) && !isComplete;

  const sharePill = onShare ? (
    <HeroPill icon={<Share aria-hidden />} label={t('share_a_link')} onClick={onShare} />
  ) : null;

  return (
    <HeroCard className="w-full pb-5" data-testid="registration-hero-card">
      {(isComplete || dueDate) && (
        <div className="mb-3 flex items-center">
          {isComplete ? (
            <Tag
              tone="success"
              fill="solid"
              casing="sentence"
              size="sm"
              className="font-semibold"
            >
              {t('registration_completed')}
            </Tag>
          ) : (
            <Tag
              tone="glass"
              casing="sentence"
              size="sm"
              className="h-7 px-3 font-medium [&>svg]:size-3.5 [&>svg]:text-[var(--chekin-on-scene-text-brand)]"
            >
              <Clock aria-hidden />
              {t('registration_due_before', {date: dueDate})}
            </Tag>
          )}
        </div>
      )}

      <h2 className="mb-3.5 text-[18px] font-semibold leading-[26px] text-white">
        <Trans
          i18nKey="hero_guests_registered"
          count={total}
          values={{done: registered}}
          components={[<span key="done" className="mr-0.5 text-[30px] font-bold" />]}
        />
      </h2>

      <SegmentedProgress
        aria-label={t('hero_guests_registered_progress')}
        className="mb-3"
        style={
          {
            '--segmented-progress-gap': total > TIGHT_GAP_ABOVE ? '3px' : '6px',
          } as CSSProperties
        }
        segments={buildSegments(registered, pending, total)}
      />

      {isComplete && (
        <p className="text-[14px] leading-[19px] text-[color:var(--chekin-on-scene-text-muted)]">
          {t('hub_hero_done_sentence')}
        </p>
      )}

      {showShare && <div className="mt-4 grid lg:hidden">{sharePill}</div>}

      {showShare && (
        <div className="mt-4 hidden items-center justify-between gap-5 rounded-[var(--chekin-radius-lg)] bg-[color-mix(in_srgb,var(--chekin-color-white)_10%,transparent)] px-4 py-3.5 backdrop-blur-[6px] lg:flex">
          <p className="max-w-[520px] text-[14px] leading-5 text-[color:var(--chekin-on-scene-text-muted)]">
            {t('hub_share_hint')}
          </p>
          <div className="shrink-0 [&_.button]:px-3.5">{sharePill}</div>
        </div>
      )}
    </HeroCard>
  );
}

export {RegistrationHeroCard};
